// ─── Telegram Sender ──────────────────────────────────────────
// Singleton library — callable from anywhere (engines, hooks, components).
// Sends formatted messages to Telegram via the send-telegram-alert edge function.
// Telegram config (chat ID + bot token) is read from user_connections in Supabase.

import { supabase } from '@/lib/supabase';

// ─── Notification categories (multi-account routing) ──────────
// Each category maps to one kind of alert. Telegram accounts subscribe to
// one or more categories (or "all"). The edge function fans out each alert
// to every active account subscribed to its category.
export const TELEGRAM_CATEGORIES = {
  TMA50_CROSS: 'tma50-cross',
  MA_CONVERGENCE: 'ma-convergence',
  PERFECT_CONVERGENCE: 'perfect-convergence',
  BOX: 'box',
  BOX_VS_TMA50: 'box-vs-tma50',
  ORDER_MODIFY: 'order-modify',
  TEST: 'test',
  AUTOCHIN: 'autochin',
  ALL: 'all',
} as const;

// ─── Types ────────────────────────────────────────────────────

export interface TelegramMessageOptions {
  /** Target admin email whose Telegram config to use. Defaults to admin@chin.vn */
  targetEmail?: string;
  /** Notification category — drives multi-account fan-out routing. */
  category?: string;
  /** Optional explicit idempotency key. If omitted, auto-derived from message content. */
  dedupKey?: string;
  /** Optional explicit chat override — used by the per-account "Test" button. */
  chatIdOverride?: string;
  /** Optional explicit bot token override — used by the per-account "Test" button. */
  botTokenOverride?: string;
}

export interface FormattedMessageParts {
  /** Timeframe label, e.g. "1H", "4H", "1D" */
  timeframe?: string;
  /** Trading symbol, e.g. "EURUSD", "XAUUSD" */
  symbol?: string;
  /** Signal direction */
  signal?: 'BUY' | 'SELL' | 'NEUTRAL';
  /** Entry/current price */
  price?: number;
  /** Strategy name */
  strategy?: string;
  /** Main message body */
  message: string;
}

export interface TelegramResult {
  success: boolean;
  error?: string;
  /** Number of chats the message was actually delivered to (fan-out). */
  sentTo?: number;
  /** Total number of subscribed chats for the category (fan-out). */
  total?: number;
}

// ─── Edge function invoke ─────────────────────────────────────

const EDGE_FN = 'send-telegram-alert';
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

/**
 * Deterministic string hash (djb2) → used to derive a stable idempotency key
 * from message content, so duplicate sends (client fallback, or two devices
 * firing the same event) collapse to the same key and get deduped server-side.
 */
function hashString(str: string): string {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h.toString(36);
}

/**
 * Send a raw message string to Telegram.
 * Uses direct fetch as primary (more reliable error visibility),
 * with supabase.functions.invoke as fallback.
 * Returns { success: true } on success, { success: false, error } on failure.
 * Never throws — all errors are caught and returned.
 */
export async function sendTelegram(message: string, options?: TelegramMessageOptions): Promise<TelegramResult> {
  if (!message || !message.trim()) {
    console.warn('[TelegramSender] Empty message — skipping');
    return { success: false, error: 'Empty message' };
  }

  // ── Validate env vars before attempting ──
  if (!SUPABASE_URL) {
    console.error('[TelegramSender] ❌ VITE_PUBLIC_SUPABASE_URL is not set!');
    return { success: false, error: 'Missing SUPABASE_URL env var' };
  }
  if (!SUPABASE_ANON_KEY) {
    console.error('[TelegramSender] ❌ VITE_PUBLIC_SUPABASE_ANON_KEY is not set!');
    return { success: false, error: 'Missing SUPABASE_ANON_KEY env var' };
  }

  // ── Idempotency key: explicit override, else derive from content ──
  // Same logical message → same key → edge function dedups within a short TTL.
  // This collapses the direct-fetch + fallback double-send into a single delivery.
  const dedupKey = options?.dedupKey || `msg:${hashString(message.trim())}`;

  // ── Primary: direct fetch for full error visibility ───────
  try {
    const url = `${SUPABASE_URL}/functions/v1/${EDGE_FN}`;
    console.log(
      `[TelegramSender] 📡 DIRECT FETCH → ${url} ` +
      `msgLen=${message.trim().length} targetEmail=${options?.targetEmail || 'default'}`
    );

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        message: message.trim(),
        target_email: options?.targetEmail,
        category: options?.category,
        chat_id_override: options?.chatIdOverride,
        bot_token_override: options?.botTokenOverride,
        dedup_key: dedupKey,
      }),
    });

    const responseText = await response.text();
    console.log(
      `[TelegramSender] 📡 DIRECT FETCH RESPONSE — status=${response.status} ` +
      `ok=${response.ok} bodyLen=${responseText.length} ` +
      `body=${responseText.slice(0, 500)}`
    );

    let parsed: TelegramResult | null = null;
    try {
      parsed = JSON.parse(responseText) as TelegramResult;
    } catch {
      console.warn('[TelegramSender] Response is not valid JSON:', responseText.slice(0, 200));
    }

    if (!response.ok) {
      const errMsg = parsed?.error || responseText || `HTTP ${response.status}`;
      console.error('[TelegramSender] Direct fetch failed:', errMsg);
      throw new Error(`Direct fetch failed: ${errMsg}`);
    }

    if (parsed && !parsed.success) {
      console.warn('[TelegramSender] Edge function returned success=false:', parsed.error);
      return parsed;
    }

    console.log('[TelegramSender] ✅ Direct fetch success');
    return parsed || { success: true };
  } catch (fetchErr) {
    const fetchMsg = fetchErr instanceof Error ? fetchErr.message : String(fetchErr);
    console.warn('[TelegramSender] Direct fetch error:', fetchMsg);

    // ── Fallback: supabase.functions.invoke ──────────────────
    try {
      console.log(`[TelegramSender] 📡 FALLBACK invoke "${EDGE_FN}"...`);

      const { data, error } = await supabase.functions.invoke<TelegramResult>(EDGE_FN, {
        body: {
          message: message.trim(),
          target_email: options?.targetEmail,
          category: options?.category,
          chat_id_override: options?.chatIdOverride,
          bot_token_override: options?.botTokenOverride,
          dedup_key: dedupKey,
        },
      });

      console.log(
        `[TelegramSender] 📡 FALLBACK RESULT — error=${error?.message || 'null'} ` +
        `data=${JSON.stringify(data)}`
      );

      if (error) {
        console.error('[TelegramSender] Fallback invoke error:', error.message);
        return { success: false, error: `Direct: ${fetchMsg}; Invoke: ${error.message}` };
      }

      if (!data?.success) {
        console.warn('[TelegramSender] Fallback returned success=false:', data?.error);
      }

      return data ?? { success: false, error: `Direct: ${fetchMsg}; Invoke: No response` };
    } catch (invokeErr) {
      const invokeMsg = invokeErr instanceof Error ? invokeErr.message : String(invokeErr);
      console.error('[TelegramSender] Both direct fetch AND invoke failed:', invokeMsg);
      return { success: false, error: `Direct: ${fetchMsg}; Invoke: ${invokeMsg}` };
    }
  }
}

// ─── Message formatting ───────────────────────────────────────

/**
 * Build a formatted HTML message for Telegram from structured parts.
 * Telegram supports a subset of HTML tags: <b>, <i>, <u>, <s>, <code>, <pre>, <a>
 *
 * Output example:
 *   📊 [1H] <b>EURUSD</b> 🟢 BUY @ 1.0850
 *   Strategy: MA Cross v2
 *   MA crossover detected, RSI confirms bullish momentum
 */
export function formatTelegramMessage(parts: FormattedMessageParts): string {
  const lines: string[] = [];

  // ── Header line ──────────────────────────────────────────
  const headerPieces: string[] = [];

  headerPieces.push('📊');

  if (parts.timeframe) {
    headerPieces.push(`[${parts.timeframe}]`);
  }

  if (parts.symbol) {
    headerPieces.push(`<b>${parts.symbol}</b>`);
  }

  if (parts.signal) {
    const emoji = parts.signal === 'BUY' ? '🟢' : parts.signal === 'SELL' ? '🔴' : '⚪';
    headerPieces.push(`${emoji} ${parts.signal}`);
  }

  if (parts.price !== undefined) {
    headerPieces.push(`@ ${parts.price}`);
  }

  lines.push(headerPieces.join(' '));

  // ── Strategy line ────────────────────────────────────────
  if (parts.strategy) {
    lines.push(`Strategy: <i>${parts.strategy}</i>`);
  }

  // ── Message body ─────────────────────────────────────────
  lines.push('');
  lines.push(parts.message);

  return lines.join('\n');
}

// ─── Convenience: build and send in one call ───────────────────

/**
 * Format a structured message AND send it to Telegram in one call.
 * Combines formatTelegramMessage + sendTelegram.
 */
export async function sendFormattedTelegram(
  parts: FormattedMessageParts,
  options?: TelegramMessageOptions,
): Promise<TelegramResult> {
  const message = formatTelegramMessage(parts);
  console.log(`[sendFormattedTelegram] Formatted message (${message.length} chars):\n${message}`);
  const result = await sendTelegram(message, options);
  console.log(`[sendFormattedTelegram] Result:`, JSON.stringify(result));
  return result;
}

// ─── Named export for tree-shaking friendly imports ────────────

const telegramSender = {
  send: sendTelegram,
  format: formatTelegramMessage,
  sendFormatted: sendFormattedTelegram,
};

export default telegramSender;